import React, { useEffect, useMemo, useState } from "react";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import { getOptimizePackagePlan } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";

import { AlertV4 } from "../common/ui";

import {
  ACTIONS,
  BTN_ACTS,
  EMPTY_SALE_FILTER,
  SEQUENCE_NAME,
  type PickedState,
  type RunSequence,
  type SaleFilter,
  type SetProp,
  type State,
} from "../common/sequence/PackageV4";
import {
  amountText,
  deleteBlockText,
  isComparable,
  isPaid,
  orderableOf,
  PACKAGE_PAGE_SIZE,
  totalOf,
  unpaidOrderOf,
  type HistoryFilter,
  type MasterOption,
  type PackageItem,
  type PackageOrder,
  type PackageTabKey,
} from "./packageModelV4";
import ModalPackageDetailV4 from "./ModalPackageDetailV4";
import PackageHistoryTabV4 from "./PackageHistoryTabV4";
import PackageSaleTabV4 from "./PackageSaleTabV4";
import PackageSelectedTabV4 from "./PackageSelectedTabV4";

import "./_hisv4-package.scss";

// Types
type CardPackageV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  PackageV4Sequence?: State["PackageV4Sequence"];
  masterOptions?: Record<string, any>;
  patientId?: number;
  // callback
  runSequence: RunSequence;
  setProp?: SetProp;
  onGoAppointments?: () => void;
};

// Const
const ERROR_KEYS = [
  BTN_ACTS.SEARCH_SALE,
  BTN_ACTS.ADD,
  BTN_ACTS.LOAD_ORDERS,
  BTN_ACTS.OPTIMIZE,
  BTN_ACTS.ORDER,
];

const CardPackageV4 = (props: CardPackageV4Props) => {
  const seq = props.PackageV4Sequence;

  const [tab, setTab] = useState<PackageTabKey>("sale");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState<SaleFilter>(EMPTY_SALE_FILTER);
  const [detailOf, setDetailOf] = useState<PackageItem | null>(null);
  const [checked, setChecked] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [openCards, setOpenCards] = useState<number[]>([]);
  const [openLog, setOpenLog] = useState<number | null>(null);

  const orders = seq?.orders ?? [];
  const serviceTypes: MasterOption[] = props.masterOptions?.packageServiceType ?? [];
  const packageTypes: MasterOption[] = props.masterOptions?.packageType ?? [];
  const checkedOrders = orders.filter((order) => checked.includes(order.id));
  const paidOrders = orders.filter(isPaid);
  const page = seq?.page ?? 1;
  const saleTotal = seq?.saleTotal ?? 0;
  const pageCount = Math.max(1, Math.ceil(saleTotal / PACKAGE_PAGE_SIZE));
  // ยังไม่โหลดของผู้ป่วยคนนี้ = กำลังโหลด
  const pending = seq?.patientId !== props.patientId;
  const saleLoading =
    pending || props.buttonLoadCheck?.[BTN_ACTS.SEARCH_SALE] === BLC_STATUS.LOADING;
  const ordersLoading =
    pending || props.buttonLoadCheck?.[BTN_ACTS.LOAD_ORDERS] === BLC_STATUS.LOADING;
  const optimize = seq?.optimize;
  const compareOrders = checkedOrders.filter(isComparable);
  const compareKey = compareOrders.map((order) => `${order.id}x${order.quantity}`).join(",");
  const detail = seq?.detail && seq.detail.id === detailOf?.id ? seq.detail : null;

  // หัวหน้า
  const tabs: { key: PackageTabKey; n: number; label: string; hint: string }[] = [
    {
      key: "sale",
      n: 1,
      label: "แพ็กเกจที่เปิดขาย",
      hint: `เลือกแพ็กเกจที่ต้องการ (${saleTotal} รายการ)`,
    },
    {
      key: "selected",
      n: 2,
      label: "แพ็กเกจที่เลือก",
      hint: `ทำนัด · สั่ง Order (${orders.length} รายการ)`,
    },
    {
      key: "history",
      n: 3,
      label: "ประวัติและยอดคงเหลือ",
      hint: `ใช้สิทธิ์ · ดูคงเหลือ (${paidOrders.length} แพ็กเกจ)`,
    },
  ];

  // Memo
  const plan = useMemo(() => {
    if (!optimize?.packages.length) return null;
    const planOrders = orders.filter((order) => optimize.orderIds.includes(order.id));
    return getOptimizePackagePlan(planOrders as any, optimize.packages);
  }, [optimize, orders]);

  // Effect
  useEffect(() => {
    if (!props.patientId) return;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patientId,
    });
    setDraft(EMPTY_SALE_FILTER);
    setChecked([]);
    setCompareOpen(false);
    setMessage("");
  }, [props.patientId]);

  // คำนวณใหม่เมื่อรายการที่เลือกหรือจำนวนเปลี่ยน
  useEffect(() => {
    if (!compareOpen || !compareKey) return;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.OPTIMIZE,
      orderIds: compareOrders.map((order) => order.id),
    });
  }, [compareOpen, compareKey]);

  // Handler
  const handleChangeTab = (key: PackageTabKey) => {
    setTab(key);
    setMessage("");
    for (const errorKey of [BTN_ACTS.ADD, BTN_ACTS.OPTIMIZE, BTN_ACTS.ORDER]) {
      if (props.errorMessage?.[errorKey]) props.setProp?.(`errorMessage.${errorKey}`, null);
    }
  };

  const handleGoSale = () => {
    handleChangeTab("sale");
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  const handleSearch = (filter: SaleFilter, toPage = 1) => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SEARCH_SALE,
      filter,
      page: toPage,
    });
  };

  const handleClear = () => {
    setDraft(EMPTY_SALE_FILTER);
    handleSearch(EMPTY_SALE_FILTER);
  };

  const handleChangePage = (toPage: number) => {
    if (toPage < 1 || toPage > pageCount || toPage === page) return;
    handleSearch(seq?.filter ?? EMPTY_SALE_FILTER, toPage);
  };

  const handleOpenDetail = (item: PackageItem) => {
    setDetailOf(item);
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.DETAIL, packageId: item.id });
  };

  const handleCloseDetail = () => {
    setDetailOf(null);
  };

  const handleAdd = (item: PackageItem) => {
    if (unpaidOrderOf(orders, item.id)) {
      setMessage(`เลือก ${item.name} ไว้แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`);
      return;
    }
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.ADD,
      item,
      onDone: (added) =>
        setMessage(
          added
            ? `เลือก ${item.name} แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`
            : `เลือก ${item.name} ไว้แล้ว · ไปแท็บ 2 เพื่อดูรายการที่เลือก`,
        ),
    });
  };

  const handleCheck = (order: PackageOrder, value: boolean) => {
    setChecked(value ? [...checked, order.id] : checked.filter((id) => id !== order.id));
  };

  const handleChangeQty = (order: PackageOrder, step: number) => {
    const quantity = Number(order.quantity) + step;
    if (isPaid(order) || quantity < 1) return;
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.UPDATE_QTY, order, quantity });
  };

  const handleRemove = (order: PackageOrder) => {
    if (deleteBlockText(order)) return;
    setChecked(checked.filter((id) => id !== order.id));
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.REMOVE, order });
  };

  const handleCompare = () => {
    setCompareOpen(!compareOpen);
  };

  const handleOrder = () => {
    const sendOrders = orderableOf(checkedOrders);
    if (!sendOrders.length) {
      setMessage("แพ็กเกจที่เลือกยังไม่ชำระเงินหรือไม่มีสิทธิ์คงเหลือ · สั่ง Order ไม่ได้");
      return;
    }
    setMessage("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.ORDER,
      orderIds: sendOrders.map((order) => order.id),
      onDone: () => {
        setChecked([]);
        setMessage(
          `ส่ง ${sendOrders.length} แพ็กเกจเข้า Order แล้ว · ยอด ${amountText(totalOf(sendOrders))} บาท`,
        );
      },
    });
  };

  const handleToggleCard = (id: number) => {
    setOpenCards(
      openCards.includes(id) ? openCards.filter((row) => row !== id) : [...openCards, id],
    );
  };

  // Render
  const renderTab = () => {
    if (tab === "sale") {
      return (
        <PackageSaleTabV4
          // data
          items={seq?.saleItems ?? []}
          orders={orders}
          draft={draft}
          page={page}
          pageCount={pageCount}
          total={saleTotal}
          loading={saleLoading}
          serviceTypes={serviceTypes}
          packageTypes={packageTypes}
          buttonLoadCheck={props.buttonLoadCheck}
          // callback
          onChangeDraft={setDraft}
          onSearch={handleSearch}
          onClear={handleClear}
          onChangePage={handleChangePage}
          onOpenDetail={handleOpenDetail}
          onAdd={handleAdd}
        />
      );
    }

    if (tab === "selected") {
      return (
        <PackageSelectedTabV4
          // data
          orders={orders}
          checked={checked}
          loading={ordersLoading}
          ordering={props.buttonLoadCheck?.[BTN_ACTS.ORDER] === BLC_STATUS.LOADING}
          compareOpen={compareOpen}
          optimizing={props.buttonLoadCheck?.[BTN_ACTS.OPTIMIZE] === BLC_STATUS.LOADING}
          optimized={!!seq?.optimize}
          plan={plan}
          // callback
          onCheck={handleCheck}
          onChangeQty={handleChangeQty}
          onRemove={handleRemove}
          onGoSale={handleGoSale}
          onGoAppointments={props.onGoAppointments}
          onOrder={handleOrder}
          onCompare={handleCompare}
        />
      );
    }

    return (
      <PackageHistoryTabV4
        // data
        orders={orders}
        usageLogs={seq?.usageLogs ?? []}
        loading={ordersLoading}
        filter={historyFilter}
        openCards={openCards}
        openLog={openLog}
        // callback
        onChangeFilter={setHistoryFilter}
        onToggleCard={handleToggleCard}
        onToggleLog={setOpenLog}
      />
    );
  };

  const renderSteps = () => (
    <div className="hv4-pkg-steps" role="tablist" aria-label="ขั้นตอน Package">
      {tabs.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={tab === item.key}
          className={`hv4-pkg-step${tab === item.key ? " hv4-pkg-step--on" : ""}`}
          onClick={() => handleChangeTab(item.key)}
        >
          <span className="hv4-pkg-step-n">{item.n}</span>
          <span className="hv4-pkg-step-text">
            <b>{item.label}</b>
            <small>{item.hint}</small>
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="hv4-pkg">
      <h1 className="hv4-pkg-h1">Package</h1>
      {renderSteps()}
      {message && (
        <div className="hv4-pkg-msg" role="status">
          {message}
        </div>
      )}
      {ERROR_KEYS.map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => handleCloseError(key)}
        />
      ))}
      {renderTab()}
      {detailOf && (
        <ModalPackageDetailV4
          key={detailOf.id}
          // data
          item={detailOf}
          detail={detail}
          loading={props.buttonLoadCheck?.[BTN_ACTS.DETAIL] === BLC_STATUS.LOADING}
          error={props.errorMessage?.[BTN_ACTS.DETAIL]}
          serviceTypes={serviceTypes}
          packageTypes={packageTypes}
          // callback
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

CardPackageV4.displayName = "CardPackageV4";

export default React.memo(CardPackageV4);
